import { useCallback, useState } from 'react'
import { useAccountContext } from 'src/components/account/context'
import { useWishlistContext } from 'src/components/contexts/wishlist-context'

import { axiosClient } from '../services/axiosInstance'

function useWishlist() {
  const { isLoggedIn } = useAccountContext()
  const [isEmptyWishlist, setIsEmptyWishlist] = useState<boolean>(false)
  const { wishlistId } = useWishlistContext()

  const getWishlist = useCallback(async () => {
    if (!isLoggedIn) {
      return null
    }

    try {
      const { data } = await axiosClient
        .get('/api/vtex/store/v1/app/wishlists', {
          params: { source: 'website' },
        })
        .then((res) => {
          if (res?.data?.products?.length === 0) {
            setIsEmptyWishlist(true)
          }

          return res
        })

      return { data }
    } catch (error) {
      throw new Error(`[API Error | Get Wishlist]', ${error}`)
    }
  }, [isLoggedIn, setIsEmptyWishlist])

  const resolveWishlistId = async () => {
    if (wishlistId !== '') {
      return wishlistId
    }

    const wishlistData = await getWishlist()
    const fetchedWishlistId = wishlistData?.data?.id ?? ''

    if (fetchedWishlistId === '') {
      throw new Error('[API Error | Wishlist ID]')
    }

    return fetchedWishlistId
  }

  const addProductToWishlist = async (productId: string) => {
    if (!isLoggedIn) {
      return null
    }

    try {
      const targetWishlistId = await resolveWishlistId()

      const { data } = await axiosClient.post(
        `/api/vtex/store/v1/app/wishlists/${targetWishlistId}`,
        { productId },
        { params: { source: 'website', c: 1 } }
      )

      return data
    } catch (error) {
      throw new Error(`[API Error | Add Product To Wishlist]', ${error}`)
    }
  }

  const removeProductFromWishlist = async (productId: string) => {
    if (!isLoggedIn) {
      return null
    }

    try {
      const targetWishlistId = await resolveWishlistId()

      const { data } = await axiosClient.delete(
        `/api/vtex/store/v1/app/wishlists/${targetWishlistId}`,
        { params: { source: 'website' }, data: { productId } }
      )

      return data
    } catch (error) {
      throw new Error(`[API Error | Remove Product From Wishlist]', ${error}`)
    }
  }

  return {
    getWishlist,
    addProductToWishlist,
    removeProductFromWishlist,
    isEmptyWishlist,
  }
}

export default useWishlist
